import { doGET, doPOST, doPUT } from '../../utils/HttpUtils';
import {
    ExternalCatalogIntegration,
    ExternalCatalogItemType,
    ExternalCatalogMetadata,
    ExternalCatalogRow,
} from '../../types/externalCatalog';

const METADATA_BASE = '/external-catalog-items';

function unwrap<T>(res: any): T | null {
    if (res?.error || res?.status >= 400) return null;
    const payload = res?.data?.data ?? res?.data;
    return payload ?? null;
}

export async function fetchExternalCatalogMetadata(
    itemType: ExternalCatalogItemType,
    integration: ExternalCatalogIntegration,
    corporateId?: string,
    masterOnly?: boolean
) {
    const query = new URLSearchParams({
        integration,
        itemType,
    });
    if (corporateId) query.set('corporateId', corporateId);
    if (masterOnly) query.set('masterOnly', 'true');
    const response = await doGET(`${METADATA_BASE}?${query.toString()}`);
    return (unwrap<ExternalCatalogMetadata[]>(response) || []) as ExternalCatalogMetadata[];
}

export async function createExternalCatalogMetadata(data: ExternalCatalogMetadata) {
    const response = await doPOST(METADATA_BASE, data);
    return unwrap<ExternalCatalogMetadata>(response);
}

export async function updateExternalCatalogMetadata(
    id: string,
    data: Omit<Partial<ExternalCatalogMetadata>, 'gender' | 'applicableAge' | 'markupPercentage' | 'customMrp' | 'campId' | 'campIds'> & {
        gender?: ExternalCatalogMetadata['gender'] | null;
        applicableAge?: number | null;
        markupPercentage?: number | null;
        customMrp?: number | null;
        customPrice?: number | null;
        markupAmount?: number | null;
        campId?: string | null;
        campIds?: string[] | null;
        status?: ExternalCatalogMetadata['status'];
    }
) {
    const response = await doPUT(`${METADATA_BASE}/${id}`, data);
    return unwrap<ExternalCatalogMetadata>(response);
}

export async function updateCorporateCatalogMetadata(
    catalogItemId: string,
    corporateId: string,
    data: Parameters<typeof updateExternalCatalogMetadata>[1]
) {
    const response = await doPUT(
        `${METADATA_BASE}/${catalogItemId}/corporates/${corporateId}`,
        data
    );
    return unwrap<ExternalCatalogMetadata>(response);
}

export async function updatePublicCatalogMetadata(
    catalogItemId: string,
    data: Parameters<typeof updateExternalCatalogMetadata>[1]
) {
    const response = await doPUT(
        `${METADATA_BASE}/${catalogItemId}/public-assignment`,
        data
    );
    return unwrap<ExternalCatalogMetadata>(response);
}

export async function syncExternalCatalog(
    integration?: ExternalCatalogIntegration,
    itemType?: ExternalCatalogItemType
) {
    const response = await doPOST(`${METADATA_BASE}/sync`, {
        ...(integration ? { integration } : {}),
        ...(itemType ? { itemType } : {}),
    });
    return unwrap<Array<{
        integration: ExternalCatalogIntegration;
        itemType: ExternalCatalogItemType;
        received: number;
        created: number;
        updated: number;
        disabled: number;
    }>>(response) || [];
}

export async function fetchExternalCatalogRows(
    integration: ExternalCatalogIntegration,
    itemType: ExternalCatalogItemType,
    corporateId?: string,
    masterOnly?: boolean
): Promise<ExternalCatalogRow[]> {
    const metadataItems = await fetchExternalCatalogMetadata(
        itemType,
        integration,
        corporateId,
        masterOnly
    );

    return metadataItems
        .map((metadata) => {
            return {
                _id: metadata._id,
                integration,
                itemType,
                externalItemId: metadata.externalItemId,
                providerName: metadata.providerName || metadata.name || '',
                termsAndConditions: metadata.termsAndConditions,
                name: metadata.name || metadata.providerName || '',
                price: metadata.providerPrice,
                customPrice: metadata.customPrice,
                customMrp: metadata.customMrp,
                markupPercentage: metadata.markupPercentage,
                markupAmount: metadata.markupAmount,
                image: metadata.image || metadata.providerImage,
                coverImage: metadata.coverImage || metadata.providerCoverImage,
                gender: metadata.gender,
                applicableAge: metadata.applicableAge,
                campId: metadata.campId,
                campIds: metadata.campIds,
                pinned: metadata.pinned ?? false,
                enabled: metadata.enabled ?? false,
                status: metadata.status ?? 1,
                skipReport: metadata.skipReport,
                skipHealthCertificate: metadata.skipHealthCertificate,
                reportEscalationHours: metadata.reportEscalationHours,
                corporateId: metadata.corporateId,
                catalogAssignmentId: metadata.catalogAssignmentId,
                catalogAssignmentOverrides: metadata.catalogAssignmentOverrides,
                createdAt: metadata.createdAt,
                updatedAt: metadata.updatedAt,
            };
        })
        .sort((a, b) => {
            const pinnedDelta = Number(Boolean(b.pinned)) - Number(Boolean(a.pinned));
            if (pinnedDelta !== 0) {
                return pinnedDelta;
            }

            const nameCompare = (a.name || '').localeCompare(b.name || '');
            if (nameCompare !== 0) {
                return nameCompare;
            }

            return a.externalItemId.localeCompare(b.externalItemId);
        });
}

export async function fetchCorporateExternalCatalogPackages(corporateId: string): Promise<ExternalCatalogRow[]> {
    if (!corporateId) return [];

    const query = new URLSearchParams({
        itemType: 'package',
        corporateId,
        enabled: 'true',
        status: '2',
    });
    const response = await doGET(`${METADATA_BASE}?${query.toString()}`);
    const metadataItems = (unwrap<ExternalCatalogMetadata[]>(response) || []) as ExternalCatalogMetadata[];

    return metadataItems
        .map((metadata) => ({
            _id: metadata._id,
            integration: metadata.integration,
            itemType: 'package' as const,
            externalItemId: metadata.externalItemId,
            providerName: metadata.providerName || metadata.name || '',
            name: metadata.name || metadata.providerName || metadata.externalItemId,
            price: metadata.providerPrice,
            customPrice: metadata.customPrice,
            customMrp: metadata.customMrp,
            markupPercentage: metadata.markupPercentage,
            markupAmount: metadata.markupAmount,
            image: metadata.image || metadata.providerImage,
            coverImage: metadata.coverImage || metadata.providerCoverImage,
            gender: metadata.gender,
            campId: metadata.campId,
            campIds: metadata.campIds,
            pinned: metadata.pinned ?? false,
            enabled: metadata.enabled ?? false,
            status: metadata.status ?? 1,
            skipReport: metadata.skipReport,
            skipHealthCertificate: metadata.skipHealthCertificate,
            corporateId: metadata.corporateId,
            catalogAssignmentId: metadata.catalogAssignmentId,
            catalogAssignmentOverrides: metadata.catalogAssignmentOverrides,
            createdAt: metadata.createdAt,
            updatedAt: metadata.updatedAt,
        }))
        .sort((a, b) => Number(Boolean(b.pinned)) - Number(Boolean(a.pinned)) || a.name.localeCompare(b.name));
}
